import { useEffect, useRef } from "react";
import { DRIVER_TAB_HARDWARE_RESYNC_EVENT } from "@eweadn-qiankun/shared-utils";
import {
  deviceFingerprintsMatchSnapshot,
  writeOwnerResyncSnapshot,
} from "./ownerResyncSnapshot";

/** 与 useKeyboardLayout 保持一致，供 Owner 接管后强拉虚拟键盘布局 */
export const KEYBOARD_DEVICE_LAYOUT_REFRESH_EVENT =
  "keyboard-device-layout-refresh";

export const DEVICE_SESSION_STANDBY_CHANGED_EVENT =
  "eweadn:deviceSessionStandbyChanged";

export const OWNER_RESYNC_LOADING_CHANGED_EVENT =
  "eweadn:ownerResyncLoadingChanged";

/** HID 未就绪时的有限重试间隔（ms） */
export const OWNER_TAKEOVER_RETRY_DELAYS_MS = [400, 900, 1500];

export type OwnerResyncProbeResult = {
  valid: boolean;
  fingerprint: string;
};

export type OwnerResyncParticipant = {
  id: string;
  enabled?: () => boolean;
  probe: () => Promise<OwnerResyncProbeResult | null>;
  commit: () => void | Promise<void>;
  /** 当前 UI 指纹；与设备指纹不一致时必须 commit（保证虚拟键盘正确） */
  getUiFingerprint?: () => string | null;
};

declare global {
  interface Window {
    __eweadn_ownerResyncLoading?: boolean;
  }
}

let ownerResyncLoading = false;
let coordinatorInstalled = false;
let coordinatorRunning = false;
let scheduleTimer: ReturnType<typeof setTimeout> | null = null;
const participants = new Map<string, OwnerResyncParticipant>();

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

export function isOwnerResyncLoading(): boolean {
  if (typeof window !== "undefined" && window.__eweadn_ownerResyncLoading) {
    return true;
  }
  return ownerResyncLoading;
}

function setOwnerResyncLoading(active: boolean): void {
  ownerResyncLoading = active;
  if (typeof window !== "undefined") {
    window.__eweadn_ownerResyncLoading = active;
    window.dispatchEvent(
      new CustomEvent(OWNER_RESYNC_LOADING_CHANGED_EVENT, {
        detail: { loading: active },
      })
    );
  }
}

function getActiveParticipants(): OwnerResyncParticipant[] {
  return Array.from(participants.values()).filter((p) => {
    try {
      return p.enabled?.() !== false;
    } catch {
      return false;
    }
  });
}

async function probeAll(): Promise<{
  valid: boolean;
  fingerprints: Record<string, string>;
}> {
  const active = getActiveParticipants();
  const fingerprints: Record<string, string> = {};
  for (const p of active) {
    try {
      const result = await p.probe();
      if (!result?.valid || !result.fingerprint) {
        return { valid: false, fingerprints: {} };
      }
      fingerprints[p.id] = result.fingerprint;
    } catch (e) {
      console.warn(`[owner-resync] probe failed: ${p.id}`, e);
      return { valid: false, fingerprints: {} };
    }
  }
  return { valid: active.length > 0, fingerprints };
}

async function commitAll(): Promise<void> {
  for (const p of getActiveParticipants()) {
    try {
      await p.commit();
    } catch (e) {
      console.warn(`[owner-resync] commit failed: ${p.id}`, e);
    }
  }
}

/**
 * 设备数据未变且 UI 已与设备一致 → 跳过 loading 与 commit。
 * 布局 participant 必须 UI 指纹 === 设备指纹，否则视为 stale 必须 commit。
 */
function shouldSkipOwnerResync(
  deviceFingerprints: Record<string, string>
): boolean {
  const active = getActiveParticipants();
  if (active.length === 0) return false;

  const ids = active.map((p) => p.id);
  if (!deviceFingerprintsMatchSnapshot(deviceFingerprints, ids)) {
    return false;
  }

  for (const p of active) {
    const devFp = deviceFingerprints[p.id];
    if (p.getUiFingerprint) {
      const uiFp = p.getUiFingerprint();
      // UI 未加载或与设备不一致 → 必须 commit，保证虚拟键盘布局正确
      if (uiFp == null || uiFp !== devFp) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Owner 接管同步：
 * 1. 单次探测 + 快照/UI 校验 → 未变则零 loading 跳过
 * 2. 有变或 UI stale → loading + 一次 commit
 * 3. HID 未就绪 → 有限次重试
 */
export async function runOwnerResyncCoordinator(): Promise<void> {
  if (coordinatorRunning || getActiveParticipants().length === 0) {
    return;
  }

  coordinatorRunning = true;

  try {
    let probe = await probeAll();

    if (probe.valid && shouldSkipOwnerResync(probe.fingerprints)) {
      return;
    }

    if (probe.valid) {
      setOwnerResyncLoading(true);
      await commitAll();
      writeOwnerResyncSnapshot(probe.fingerprints);
      return;
    }

    setOwnerResyncLoading(true);
    for (const delay of OWNER_TAKEOVER_RETRY_DELAYS_MS) {
      await sleep(delay);
      probe = await probeAll();
      if (!probe.valid) continue;

      if (shouldSkipOwnerResync(probe.fingerprints)) {
        return;
      }

      await commitAll();
      writeOwnerResyncSnapshot(probe.fingerprints);
      return;
    }
  } finally {
    coordinatorRunning = false;
    setOwnerResyncLoading(false);
  }
}

function scheduleOwnerResyncCoordinator(): void {
  if (scheduleTimer != null) {
    clearTimeout(scheduleTimer);
  }
  scheduleTimer = setTimeout(() => {
    scheduleTimer = null;
    void runOwnerResyncCoordinator();
  }, 80);
}

export function registerOwnerResyncParticipant(
  participant: OwnerResyncParticipant
): () => void {
  participants.set(participant.id, participant);
  ensureOwnerResyncCoordinatorInstalled();
  return () => {
    participants.delete(participant.id);
  };
}

function ensureOwnerResyncCoordinatorInstalled(): void {
  if (coordinatorInstalled || typeof window === "undefined") return;
  coordinatorInstalled = true;

  const onStandbyChange = (ev: Event) => {
    const standby = Boolean(
      (ev as CustomEvent<{ standby?: boolean }>).detail?.standby
    );
    if (standby) {
      return;
    }
    scheduleOwnerResyncCoordinator();
  };

  const onHardwareResync = () => {
    scheduleOwnerResyncCoordinator();
  };

  window.addEventListener(
    DEVICE_SESSION_STANDBY_CHANGED_EVENT,
    onStandbyChange
  );
  window.addEventListener(DRIVER_TAB_HARDWARE_RESYNC_EVENT, onHardwareResync);
}

/** Hook 内注册 Owner 静默探测 + 一次性 commit */
export function useOwnerResyncParticipant(
  participant: OwnerResyncParticipant | null,
  enabled: boolean = true
): void {
  const participantRef = useRef(participant);
  participantRef.current = participant;

  useEffect(() => {
    if (!enabled || !participant) return;

    return registerOwnerResyncParticipant({
      id: participant.id,
      enabled: () => participantRef.current?.enabled?.() !== false,
      probe: () => participantRef.current!.probe(),
      commit: () => participantRef.current!.commit(),
      getUiFingerprint: () =>
        participantRef.current?.getUiFingerprint?.() ?? null,
    });
  }, [enabled, participant?.id]);
}

export function fingerprintJson(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
}

/** @deprecated 使用协调器 + useOwnerResyncParticipant */
export function useOwnerTakeoverResync(
  _onResync: () => void | Promise<void>,
  _enabled: boolean = true
): void {
  /* 已由协调器替代 */
}
